import * as React from 'react';

// ---------------------------------------------------------------------------
// useColumnReorder — drag a header cell to reorder columns
// ---------------------------------------------------------------------------
//
// One delegated pointerdown listener on the scroll container (header cells
// mark themselves with a `data-dtreorderid` attribute — one lowercase word, so
// the name needs no case conversion anywhere). A 5px movement threshold
// separates drag from click-to-sort; once a drag activates, didDragRef stays
// true through pointer-up so DataTableColumnHeader's click handler (which
// fires during the same pointerup dispatch) can swallow the sort, then clears
// on a 0ms timeout.
//
// The drop target is the nearest column boundary, computed from the same
// resolved width map the rest of the table renders from (prefix sums over
// the visual column order), restricted to the contiguous reorderable span
// (consumer columns — pinned and structural columns are locked). An rAF
// loop drives edge auto-scroll, indicator updates, and the floating header
// ghost (positioned imperatively via setGhostEl) while dragging.

const DRAG_THRESHOLD_PX = 5;
const EDGE_SCROLL_ZONE_PX = 40;
const EDGE_SCROLL_SPEED_PX = 14;
// Ghost chip offset from the pointer — clears the cursor glyph without
// drifting far enough to read as detached.
const GHOST_OFFSET_X = 14;
const GHOST_OFFSET_Y = 10;

type PendingDrag = {
  columnId: string;
  startX: number;
  startY: number;
};

export function useColumnReorder({
  scrollContainerRef,
  enabled,
  visualIds,
  reorderableIds,
  widths,
  onDrop,
}: {
  scrollContainerRef: React.RefObject<unknown>;
  enabled: boolean;
  /** All visible column ids in on-screen order (left pinned, center, right pinned). */
  visualIds: string[];
  /** The contiguous run of consumer columns that may be reordered. */
  reorderableIds: string[];
  widths: Map<string, number>;
  /** beforeId = visual column the dragged column lands before; null = end of the span. */
  onDrop: (draggedId: string, beforeId: string | null) => void;
}): {
  draggingColumnId: string | null;
  dropIndicatorX: number | null;
  didDragRef: React.RefObject<boolean>;
  /**
   * Callback ref for the floating header ghost. The hook positions the
   * element imperatively (translate in scroll-content coordinates) from the
   * same rAF loop that drives auto-scroll — no React state per mousemove.
   */
  setGhostEl: (el: HTMLElement | null) => void;
} {
  const enabledRef = React.useRef(enabled);
  enabledRef.current = enabled;
  const visualIdsRef = React.useRef(visualIds);
  visualIdsRef.current = visualIds;
  const reorderableIdsRef = React.useRef(reorderableIds);
  reorderableIdsRef.current = reorderableIds;
  const widthsRef = React.useRef(widths);
  widthsRef.current = widths;
  const onDropRef = React.useRef(onDrop);
  onDropRef.current = onDrop;

  const didDragRef = React.useRef(false);
  const ghostElRef = React.useRef<HTMLElement | null>(null);
  const pointerRef = React.useRef({ x: 0, y: 0 });

  const positionGhost = React.useCallback(() => {
    const ghost = ghostElRef.current;
    const el = scrollContainerRef.current as unknown as HTMLElement | null;
    if (!ghost || !el) return;
    const rect = el.getBoundingClientRect();
    const x =
      pointerRef.current.x - rect.left + el.scrollLeft + GHOST_OFFSET_X;
    const y = pointerRef.current.y - rect.top + el.scrollTop + GHOST_OFFSET_Y;
    ghost.style.transform = `translate(${x}px, ${y}px)`;
  }, [scrollContainerRef]);

  const setGhostEl = React.useCallback(
    (el: HTMLElement | null) => {
      ghostElRef.current = el;
      // The ghost mounts mid-drag (after draggingColumnId is set) — place it
      // at the pointer immediately so it never flashes at the origin.
      if (el) positionGhost();
    },
    [positionGhost],
  );

  const [draggingColumnId, setDraggingColumnId] = React.useState<
    string | null
  >(null);
  const [dropIndicatorX, setDropIndicatorX] = React.useState<number | null>(
    null,
  );

  React.useEffect(() => {
    const el = scrollContainerRef.current as unknown as HTMLElement | null;
    if (!enabled || !el) return;

    let pending: PendingDrag | null = null;
    let active = false;
    let raf: number | null = null;
    let lastBoundaryIndex: number | null = null;
    let prevCursor = '';
    let prevUserSelect = '';

    // Boundary b sits at the left edge of visualIds[b] (b = length → table
    // end). Only boundaries touching the reorderable span are drop targets.
    const computeBoundaries = () => {
      const ids = visualIdsRef.current;
      const w = widthsRef.current;
      const reorderable = new Set(reorderableIdsRef.current);
      let firstIdx = -1;
      let lastIdx = -1;
      ids.forEach((id, i) => {
        if (reorderable.has(id)) {
          if (firstIdx === -1) firstIdx = i;
          lastIdx = i;
        }
      });
      const positions: number[] = [];
      let x = 0;
      for (const id of ids) {
        positions.push(x);
        // No fallback: `resolveColumnWidths` sets an entry for every column it
        // is handed and never deletes one, so data-table's width map is keyed
        // by exactly the ids it passes as `visualIds`.
        x += w.get(id)!;
      }
      positions.push(x);
      return { positions, firstIdx, lastIdx };
    };

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const rect = el.getBoundingClientRect();
      const pointerX = pointerRef.current.x;
      if (pointerX < rect.left + EDGE_SCROLL_ZONE_PX) {
        el.scrollLeft -= EDGE_SCROLL_SPEED_PX;
      } else if (pointerX > rect.right - EDGE_SCROLL_ZONE_PX) {
        el.scrollLeft += EDGE_SCROLL_SPEED_PX;
      }
      positionGhost();

      const contentX = pointerX - rect.left + el.scrollLeft;
      const { positions, firstIdx, lastIdx } = computeBoundaries();
      // Backstop against state drift: the id and width refs are re-read every
      // frame, so a re-render that empties the reorderable span mid-drag (the
      // consumer hides the last movable column) would leave nothing to index.
      if (firstIdx === -1) return;
      // The winning boundary's x rides along with its index, so the indicator
      // is placed from the same lookup that chose the boundary.
      let best = firstIdx;
      let bestX: number | undefined;
      let bestDist = Infinity;
      for (let b = firstIdx; b <= lastIdx + 1; b++) {
        // `positions` holds one entry per column plus the table end, and
        // `lastIdx` is an index into the same `ids` the prefix sums walked, so
        // the top of this range is the final entry rather than past it.
        const position = positions[b]!;
        const dist = Math.abs(position - contentX);
        if (dist < bestDist) {
          bestDist = dist;
          best = b;
          bestX = position;
        }
      }
      if (best !== lastBoundaryIndex && bestX !== undefined) {
        lastBoundaryIndex = best;
        setDropIndicatorX(bestX);
      }
    };

    const cleanupDrag = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      if (raf !== null) cancelAnimationFrame(raf);
      raf = null;
      if (active) {
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
        setDraggingColumnId(null);
        setDropIndicatorX(null);
      }
      pending = null;
      active = false;
      lastBoundaryIndex = null;
    };

    const onMove = (e: PointerEvent) => {
      // `pending` is set by the pointerdown that attached this listener, and
      // the only thing that clears it is `cleanupDrag`, which detaches the
      // listener in the same statement block. A move here always has a drag.
      const drag = pending!;
      pointerRef.current = { x: e.clientX, y: e.clientY };
      if (!active) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD_PX) return;
        active = true;
        didDragRef.current = true;
        prevCursor = document.body.style.cursor;
        prevUserSelect = document.body.style.userSelect;
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
        setDraggingColumnId(drag.columnId);
        raf = requestAnimationFrame(tick);
      }
      e.preventDefault();
    };

    const onUp = () => {
      // Live drag guaranteed, as in onMove. This one is registered for both
      // `pointerup` and `pointercancel`, but a pointer gets one terminal event
      // and the first call detaches both registrations.
      const wasActive = active;
      const draggedId = pending!.columnId;
      const boundary = lastBoundaryIndex;
      cleanupDrag();
      if (!wasActive) return;
      // Clear after the click/press this pointerup synthesizes has been
      // dispatched (and swallowed by the column header's didDrag check).
      setTimeout(() => {
        didDragRef.current = false;
      }, 0);
      // Backstop against frame timing: the boundary is resolved by the rAF
      // loop the arming move schedules, and rAF callbacks run after the
      // frame's input is dispatched. A press that crosses the 5px threshold
      // and releases inside one frame therefore lands here with no boundary
      // yet — drop it rather than guess an index.
      if (boundary === null) return;
      const ids = visualIdsRef.current;
      const beforeId = ids[boundary] ?? null;
      if (beforeId === draggedId) return;
      onDropRef.current(draggedId, beforeId);
    };

    const onDown = (e: PointerEvent) => {
      if (!enabledRef.current || e.button !== 0 || pending) return;
      // A dispatched pointer event on an element listener always carries an
      // Element target; the DOM type is wide because an event that was never
      // dispatched has none.
      const target = e.target as Element;
      if (target.closest('[data-dtresize]')) return;
      const cell = target.closest('[data-dtreorderid]');
      if (!cell || !el.contains(cell)) return;
      // `closest` matched the attribute selector, so the attribute is present.
      // data-table.tsx stamps `data-dtreorderid` only on ids in the same
      // reorderable set this hook is handed, so the match implies membership
      // and no second check is needed.
      const columnId = cell.getAttribute('data-dtreorderid')!;
      // No preventDefault — a plain click must still reach the sort Pressable.
      pending = { columnId, startX: e.clientX, startY: e.clientY };
      pointerRef.current = { x: e.clientX, y: e.clientY };
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    };

    el.addEventListener('pointerdown', onDown);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      cleanupDrag();
    };
  }, [enabled, scrollContainerRef, positionGhost]);

  return { draggingColumnId, dropIndicatorX, didDragRef, setGhostEl };
}
